import { Ref, ref, computed } from "vue";
import { SyuugyouBasyo, SyuugyouBasyoAndKoyouKeiyaku, SyuugyouBasyoAndKoyouKeiyakuOption, SyuugyouBasyoOption } from "@/types/SyuugyouBasyo";
import { SyuugyouBasyoService } from "@/service/SyuugyouBasyoService";

/*
 * 就業場所の取得に関するComposable
 */
export function useSyuugyouBasyoInit() {
  const loading = ref(0) // APIで読み込みをする本数
  const isError = ref(false)

  // 就業場所データ保存用
  let syuugyouBasyoList: Ref<SyuugyouBasyo[]> = ref([])
  let selectedSyuugyouBasyo: Ref<SyuugyouBasyo | null> = ref(null);
  let syuugyouBasyoWithKoyouKeiyakuList: Ref<SyuugyouBasyoAndKoyouKeiyaku[]> = ref([])
  let selectedSyuugyouBasyoWithKoyouKeiyaku: Ref<SyuugyouBasyoAndKoyouKeiyaku | null> = ref(null);

  // 就業場所オプション値保存用
  let syuugyouBasyoOptions: Ref<SyuugyouBasyoOption[]> = ref([])
  let selectedSyuugyouBasyoOption: Ref<SyuugyouBasyoOption | null> = ref(null);
  let syuugyouBasyoWithKoyouKeiyakuOptions: Ref<SyuugyouBasyoAndKoyouKeiyakuOption[]> = ref([])
  let selectedSyuugyouBasyoWithKoyouKeiyakuOption: Ref<SyuugyouBasyoAndKoyouKeiyakuOption | null> = ref(null);

  const syuugyouBasyoService = new SyuugyouBasyoService()

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある全ての就業場所を取得します
   */
  const getAll = async () => {
    loading.value++
    syuugyouBasyoList.value = await syuugyouBasyoService.getAll();
    loading.value--
  }

  /**
   * マスタに登録されている就業場所のうち、実行ユーザーに閲覧権限のある就業場所を取得します（オプション値）
   */
  const getAllOptions = async () => {
    loading.value++
    syuugyouBasyoOptions.value = await syuugyouBasyoService.getAllOptions();
    loading.value--
  }

  /**
   * 指定したユーザーの有効な雇用契約に基づき就業場所を取得します
   */
  const getByUserId = async (userId: number | null, showOldKeiyaku = false) => {
    loading.value++
    syuugyouBasyoList.value = await syuugyouBasyoService.getByUserId(userId, showOldKeiyaku);
    loading.value--
  }

  /**
   * 指定したユーザーの有効な雇用契約に基づき就業場所を取得します（オプション値）
   */
  const getOptionsByUserId = async (userId: number | null, showOldKeiyaku = false, showKeiyakuKikanForGetOptions = true) => {
    loading.value++
    syuugyouBasyoOptions.value = await syuugyouBasyoService.getOptionsByUserId(userId, showOldKeiyaku, showKeiyakuKikanForGetOptions);
    loading.value--
  }

  /**
   * 指定したユーザーの有効な雇用契約と打刻履歴に基づき就業場所を取得します
   */
  const getByDakokuAndKoyouKeiyaku = async (userId: number | null, year: number | null = null, month: number | null = null, showOldKeiyaku = false) => {
    loading.value++
    syuugyouBasyoList.value = await syuugyouBasyoService.getByDakokuAndKoyouKeiyaku(userId, year, month, showOldKeiyaku);
    loading.value--
  }

  /**
   * 指定したユーザーの有効な雇用契約と打刻履歴に基づき就業場所を取得します（オプション値）
   */
  const getOptionsByDakokuAndKoyouKeiyaku = async (userId: number | null, year: number | null = null, month: number | null = null, showOldKeiyaku = false, showKeiyakuKikanForGetOptions = true) => {
    loading.value++
    syuugyouBasyoOptions.value = await syuugyouBasyoService.getOptionsByDakokuAndKoyouKeiyaku(userId, year, month, showOldKeiyaku, showKeiyakuKikanForGetOptions);
    loading.value--
  }

  /**
   *  指定ユーザーの有効な雇用契約に基づく就業場所一覧とその雇用契約で定められた就業時間を取得します
   */
  const getByUserIdWithKoyouKeiyakuTimes = async (userId: number | null, showOldKeiyaku = false) => {
    loading.value++
    syuugyouBasyoWithKoyouKeiyakuList.value = await syuugyouBasyoService.getByUserIdWithKoyouKeiyakuTimes(userId, showOldKeiyaku);
    loading.value--
  }

  /**
   *  指定ユーザーの有効な雇用契約に基づく就業場所一覧とその雇用契約で定められた就業時間を取得します（オプション値）
   */
  const getOptionByUserIdWithKoyouKeiyakuTimes = async (userId: number | null, showOldKeiyaku = false, showKeiyakuKikanForGetOptions = true, targetDate: string | null) => {
    loading.value++
    syuugyouBasyoWithKoyouKeiyakuOptions.value = await syuugyouBasyoService.getOptionByUserIdWithKoyouKeiyakuTimes(userId, showOldKeiyaku, showKeiyakuKikanForGetOptions, targetDate);
    loading.value--
  }


  // 読み込み中か判断する
  const isLoading = computed(() => {
    return !(loading.value === 0 || isError.value)
  })

  return {
    isLoading,
    isError,
    syuugyouBasyoList,
    syuugyouBasyoOptions,
    selectedSyuugyouBasyoOption,
    selectedSyuugyouBasyo,
    syuugyouBasyoWithKoyouKeiyakuList,
    selectedSyuugyouBasyoWithKoyouKeiyaku,
    syuugyouBasyoWithKoyouKeiyakuOptions,
    selectedSyuugyouBasyoWithKoyouKeiyakuOption,
    getAllSyuugyouBasyoList: getAll,
    getAllSyuugyouBasyoOptions :getAllOptions,
    getSyuugyouBasyoListByUserId: getByUserId,
    getSyuugyouBasyoOptionByUserId: getOptionsByUserId,    
    getByDakokuAndKoyouKeiyaku,
    getOptionsByDakokuAndKoyouKeiyaku,
    getByUserIdWithKoyouKeiyakuTimes,
    getOptionByUserIdWithKoyouKeiyakuTimes,
  }
}
